import { Loader2 } from "lucide-react";
import React, { forwardRef } from "react";
import { cn } from "../../../lib/utils";
import { SaveButtonV2 } from "../button/v2/SaveButton";
import CansoEditor, { type MDXEditorMethods } from "../editor";
import { Section } from "../section/Section";
import GenerateAIButton from "./generateAIButton";
import ImproveAIButton from "./improveAIButton";

interface RemarksSectionProps {
  remarkKey: string;
  value: string;
  onChange: (value: string) => void;
  onSave?: () => void;
  onImproveAI?: () => void;
  onGenerateAI?: () => void;
  isSaving?: boolean;
  isImproving?: boolean;
  isGenerating?: boolean;
  error?: string | null;
  title: React.ReactNode;
  headerActions?: React.ReactNode;
  placeholder?: string;
  remarkType: "analysis" | "observation" | "step" | "general";
  hasUnsavedChanges?: boolean;
  sectionProps?: Partial<React.ComponentProps<typeof Section>>;
}

/**
 * RemarksSection Component (Presentational)
 *
 * Purpose: Renders a textarea field within a Section, along with optional
 * AI generation/improvement and save buttons. Handles loading and error states.
 * Receives all state and callbacks via props.
 */

const RemarksSection = forwardRef<MDXEditorMethods, RemarksSectionProps>(
  (
    {
      remarkKey,
      value,
      onChange,
      onSave,
      onImproveAI,
      onGenerateAI,
      isSaving = false,
      isImproving = false,
      isGenerating = false,
      error = null,
      title,
      headerActions,
      placeholder = "Add your remarks...",
      remarkType,
      hasUnsavedChanges,
      sectionProps = {},
    },
    ref,
  ) => {
    // Determine if any action is currently loading
    const isLoading = isSaving || isImproving || isGenerating;
    // Determine if the component should be disabled
    const isDisabled = isLoading;
    // Handle the text change
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    };

    // Determines text and icon based on loading state
    const renderButtonContent = (
      defaultText: string,
      loadingText: string,
      isLoadingFlag: boolean,
      IconComponent: React.ElementType,
    ) => (
      <>
        {isLoadingFlag ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <IconComponent className="mr-2 h-4 w-4" />
        )}
        {isLoadingFlag ? loadingText : defaultText}
      </>
    );

    const combinedHeaderActions = (
      <>
        <div className="h-4" />
        <div className="flex items-center gap-3">
          {headerActions}
          {onGenerateAI && (
            <GenerateAIButton
              onClick={onGenerateAI}
              loading={isGenerating}
              disabled={isDisabled}
            />
          )}

          {onImproveAI && (
            <ImproveAIButton
              onClick={onImproveAI}
              loading={isImproving}
              disabled={isDisabled}
            />
          )}
        </div>
      </>
    );

    return (
      <Section
        title={title}
        headerActions={combinedHeaderActions}
        bodyClassName="pt-0 pb-4"
        {...sectionProps}
      >
        <div
          aria-invalid={!!error}
          aria-describedby={error ? `${remarkKey}-remarks-error` : undefined}
          aria-label={`${remarkType.charAt(0).toUpperCase() + remarkType.slice(1)} Remarks Editor Container`}
        >
          <CansoEditor
            ref={ref}
            id={`${remarkKey}-remarks-editor`}
            key={`${remarkKey}-remarks-editor`}
            markdown={value}
            onChange={(newValue) => onChange(newValue)}
            placeholder={placeholder}
            readOnly={isDisabled}
            className={cn(
              "w-full border border-gray-200 rounded-md",
              error && "border-red-300",
            )}
            contentEditableClassName="h-[200px]"
          />
        </div>
        {!!error && (
          <p
            id={`${remarkKey}-remarks-error`}
            role="alert"
            className="mt-2.5 text-sm font-medium text-destructive rounded-md text-[#FF4D4F]"
          >
            Something went wrong, please try again
          </p>
        )}

        {onSave && (hasUnsavedChanges || isSaving) && (
          <div className="w-full min-h-[44px] flex justify-end items-center gap-5 rounded-lg py-2 px-2.5 border bg-[#F1F5F9] mt-4">
            <div className="flex flex-col items-end flex-1">
              <span className="font-sans font-semibold text-sm leading-5 tracking-normal text-[#737373]">
                {isSaving ? "Saving..." : "You have made changes, save now"}
              </span>
            </div>
            <SaveButtonV2
              onClick={onSave}
              disabled={isDisabled}
              size="md"
              loading={isSaving}
              buttonText="Save"
              aria-busy={isSaving}
            />
          </div>
        )}
      </Section>
    );
  },
);

RemarksSection.displayName = "RemarksSection";

export { RemarksSection };
